import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import './ArcadeModal.css';
import './Minesweeper.css';

const BOARD_SIZE = 10;
const MINES_COUNT = 15;

const createBoard = () => {
  const board = [];
  for (let i = 0; i < BOARD_SIZE; i++) {
    board[i] = [];
    for (let j = 0; j < BOARD_SIZE; j++) {
      board[i][j] = {
        isMine: false,
        isRevealed: false,
        isFlagged: false,
        neighborCount: 0,
      };
    }
  }
  return board;
};

const placeMines = (board) => {
  let minesPlaced = 0;
  while (minesPlaced < MINES_COUNT) {
    const row = Math.floor(Math.random() * BOARD_SIZE);
    const col = Math.floor(Math.random() * BOARD_SIZE);
    if (!board[row][col].isMine) {
      board[row][col].isMine = true;
      minesPlaced++;
    }
  }
  return board;
};

const calculateNeighbors = (board) => {
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (!board[row][col].isMine) {
        let count = 0;
        for (let i = -1; i <= 1; i++) {
          for (let j = -1; j <= 1; j++) {
            if (
              row + i >= 0 &&
              row + i < BOARD_SIZE &&
              col + j >= 0 &&
              col + j < BOARD_SIZE &&
              board[row + i][col + j].isMine
            ) {
              count++;
            }
          }
        }
        board[row][col].neighborCount = count;
      }
    }
  }
  return board;
};

const revealCell = (board, row, col) => {
  if (
    row < 0 ||
    row >= BOARD_SIZE ||
    col < 0 ||
    col >= BOARD_SIZE ||
    board[row][col].isRevealed ||
    board[row][col].isFlagged
  ) {
    return board;
  }

  board[row][col].isRevealed = true;

  if (board[row][col].neighborCount === 0 && !board[row][col].isMine) {
    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        board = revealCell(board, row + i, col + j);
      }
    }
  }

  return board;
};

const checkWin = (board) => {
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (!board[row][col].isMine && !board[row][col].isRevealed) {
        return false;
      }
    }
  }
  return true;
};

const Minesweeper = ({ isOpen, toggle }) => {
  const [board, setBoard] = useState(createBoard());
  const [gameStatus, setGameStatus] = useState('playing'); // 'playing', 'won', 'lost'
  const [flagMode, setFlagMode] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  const initializeBoard = useCallback(() => {
    let newBoard = createBoard();
    newBoard = placeMines(newBoard);
    newBoard = calculateNeighbors(newBoard);
    setBoard(newBoard);
    setGameStatus('playing');
    setStartTime(Date.now());
    setElapsedTime(0);
  }, []);

  useEffect(() => {
    if (isOpen) {
      initializeBoard();
    }
  }, [isOpen, initializeBoard]);

  useEffect(() => {
    let interval;
    if (gameStatus === 'playing' && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameStatus, startTime]);

  const handleCellClick = (row, col) => {
    if (gameStatus !== 'playing') return;

    const newBoard = [...board.map(row => [...row])];

    if (flagMode) {
      newBoard[row][col].isFlagged = !newBoard[row][col].isFlagged;
      setBoard(newBoard);
      return;
    }

    if (newBoard[row][col].isMine) {
      // Reveal all mines
      for (let i = 0; i < BOARD_SIZE; i++) {
        for (let j = 0; j < BOARD_SIZE; j++) {
          if (newBoard[i][j].isMine) {
            newBoard[i][j].isRevealed = true;
          }
        }
      }
      setBoard(newBoard);
      setGameStatus('lost');
      return;
    }

    revealCell(newBoard, row, col);
    setBoard(newBoard);

    if (checkWin(newBoard)) {
      setGameStatus('won');
    }
  };

  const getCellContent = (cell) => {
    if (cell.isFlagged && !cell.isRevealed) {
      return '🚩';
    }
    if (!cell.isRevealed) {
      return '';
    }
    if (cell.isMine) {
      return <img src="/images/machines/small_thumb.png" alt="mine" style={{ width: '16px', height: '16px', objectFit: 'contain' }} />;
    }
    if (cell.neighborCount > 0) {
      return cell.neighborCount;
    }
    return '';
  };

  const getCellClass = (cell) => {
    let classes = 'minesweeper-cell';
    if (cell.isRevealed) {
      classes += ' revealed';
      if (cell.isMine) {
        classes += ' mine';
      }
    } else {
      classes += ' hidden';
    }
    return classes;
  };

  const resetGame = () => {
    initializeBoard();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered contentClassName="arcade-modal">
      <ModalHeader toggle={toggle} className="arcade-header" tag="div">
        <span className="arcade-title">
          <span className="arcade-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>
            <i className="fas fa-bomb"></i>
          </span>
          BuscaMinis
        </span>
        <div className="arcade-actions">
          <span className="arcade-hud"><i className="fas fa-stopwatch"></i> {elapsedTime}s</span>
          <button
            type="button"
            className={`arcade-chip ${flagMode ? 'is-active' : ''}`}
            onClick={() => setFlagMode(!flagMode)}
          >
            {flagMode ? '🚩 Marcar' : '👆 Revelar'}
          </button>
          <button type="button" className="arcade-chip primary" onClick={resetGame}>
            <i className="fas fa-rotate-right"></i> Nueva
          </button>
        </div>
      </ModalHeader>
      <ModalBody className="arcade-body">
        {gameStatus === 'won' && <div className="arcade-banner win">🎉 ¡Ganaste! 🎉</div>}
        {gameStatus === 'lost' && <div className="arcade-banner lose">💥 ¡Perdiste! 💥</div>}
        {gameStatus === 'playing' && <div className="arcade-banner playing">Encuentra las {MINES_COUNT} VACWAYminis</div>}

        <div className="d-flex justify-content-center">
          <div className="minesweeper-board">
            {board.map((row, rowIndex) => (
              <div key={rowIndex} className="minesweeper-row">
                {row.map((cell, colIndex) => (
                  <div
                    key={colIndex}
                    className={getCellClass(cell)}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                  >
                    {getCellContent(cell)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <p className="arcade-hint">
          Click para revelar · Activa el modo <strong>Marcar</strong> para señalar minas
        </p>
      </ModalBody>
    </Modal>
  );
};

export default Minesweeper;